import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Checkbox } from 'components/ui/checkbox';
import { Warning } from '@phosphor-icons/react';
import {
  MaintenanceScheduleTemplate,
  MaintenanceFrequencyLabels,
  MaintenanceTypeLabels,
  MaintenanceFrequency,
  MaintenanceType,
} from 'shared/types/asset';
import { useResolveTemplateConflicts } from 'hooks/useAssets';
import { toast } from 'react-toastify';

interface TemplateConflictDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetId: string;
  assetName: string;
  conflicts: MaintenanceScheduleTemplate[];
  onResolved: () => void;
}

export const TemplateConflictDialog: React.FC<TemplateConflictDialogProps> = ({
  open,
  onOpenChange,
  assetId,
  assetName,
  conflicts,
  onResolved,
}) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const { resolveConflicts, isResolving } = useResolveTemplateConflicts();

  const handleToggle = (templateId: number) => {
    setSelectedIds((prev) =>
      prev.includes(templateId)
        ? prev.filter((id) => id !== templateId)
        : [...prev, templateId],
    );
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0) return;

    try {
      await resolveConflicts(assetId, selectedIds);
      toast.success('Onderhoudsschema\'s toegepast');
      setSelectedIds([]);
      onResolved();
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fout bij toepassen';
      toast.error(msg);
    }
  };

  const handleSkip = () => {
    setSelectedIds([]);
    onOpenChange(false);
    onResolved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Warning className="h-5 w-5 text-amber-500" />
            Conflicterende onderhoudsregels
          </DialogTitle>
          <DialogDescription>
            De voorziening <strong>{assetName}</strong> valt onder meerdere
            clusterregels die niet tegelijk van toepassing kunnen zijn. Selecteer
            welke onderhoudsregels moeten worden toegepast.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {conflicts.map((template) => (
            <label
              key={template.id}
              className="flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
            >
              <Checkbox
                checked={selectedIds.includes(template.id)}
                onCheckedChange={() => handleToggle(template.id)}
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">
                    {template.name || 'Naamloze regel'}
                  </span>
                  <Badge variant="outline" className="text-xs shrink-0">
                    {MaintenanceFrequencyLabels[template.frequency as MaintenanceFrequency] ||
                      template.frequency}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="secondary" className="text-xs">
                    {MaintenanceTypeLabels[template.maintenanceType as MaintenanceType] ||
                      template.maintenanceType}
                  </Badge>
                  {template.conditions?.rules
                    ?.filter((r) => r.type === 'cluster' || r.type === 'clusterGroup')
                    .map((rule, idx) => (
                      <span key={idx} className="text-xs text-muted-foreground">
                        {rule.type === 'cluster' ? 'Clusters' : 'Clustergroepen'}:{' '}
                        {Array.isArray(rule.value) ? rule.value.length : 1} geselecteerd
                      </span>
                    ))}
                </div>
                {template.description && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                    {template.description}
                  </p>
                )}
              </div>
            </label>
          ))}
        </div>

        <div className="flex justify-between pt-2">
          <Button variant="ghost" onClick={handleSkip} disabled={isResolving}>
            Overslaan
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedIds.length === 0 || isResolving}
          >
            {isResolving
              ? 'Toepassen...'
              : `${selectedIds.length} regel${selectedIds.length !== 1 ? 's' : ''} toepassen`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
